import React, { useState, useEffect, useRef } from "react";
import CtaButton from "./CtaButton";

function useOnScreen(ref, threshold = 0.15) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold }
    );
    const el = ref.current;
    if (el) observer.observe(el);
    return () => { if (el) observer.unobserve(el); };
  }, [ref, threshold]);
  return visible;
}

// True once the user has scrolled the page at all. On tall desktop
// screens the whole first narrative section can fit within the initial
// viewport, so an in-view check alone fades it in before any scroll. We
// additionally gate on this so narrative sections only reveal after the
// user initiates scrolling. Starts true if the page is already scrolled
// (e.g. restored position / deep link). (#98)
function useHasScrolled() {
  const [scrolled, setScrolled] = useState(() =>
    typeof window !== "undefined" && window.scrollY > 0
  );
  useEffect(() => {
    if (scrolled) return undefined;
    const onScroll = () => {
      if (window.scrollY > 0) setScrolled(true);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    // Sync in case a scroll happened between render and effect.
    if (window.scrollY > 0) setScrolled(true);
    return () => window.removeEventListener("scroll", onScroll);
  }, [scrolled]);
  return scrolled;
}

function FadeSection({ children, delay = 0, className = "", style = {} }) {
  const ref = useRef(null);
  const inView = useOnScreen(ref, 0.1);
  const hasScrolled = useHasScrolled();
  // Reveal only when the section is in view AND the user has scrolled, so
  // a section that already fits in the initial viewport (tall desktop)
  // doesn't appear until scrolling begins. The reveal latches on (stays
  // visible thereafter). Mobile is unaffected — its sections sit below
  // the fold and only enter view after scrolling anyway. (#98)
  const [revealed, setRevealed] = useState(false);
  useEffect(() => {
    if (inView && hasScrolled) setRevealed(true);
  }, [inView, hasScrolled]);
  const visible = revealed;
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(32px)",
        transition: `opacity 0.9s cubic-bezier(0.22,1,0.36,1) ${delay}s, transform 0.9s cubic-bezier(0.22,1,0.36,1) ${delay}s`,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

const styles = {
  global: `
    @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400;1,500&family=Outfit:wght@300;400;500&display=swap');

    .loore-landing * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }

    .loore-landing {
      background: var(--bg-deep);
      color: var(--text-primary);
      font-family: var(--sans);
      min-height: 100vh;
      overflow-x: hidden;
      position: relative;
    }

    .loore-landing::before {
      content: '';
      position: fixed;
      inset: 0;
      background:
        radial-gradient(ellipse 80% 60% at 50% 0%, var(--gradient-overlay-1) 0%, transparent 70%),
        radial-gradient(ellipse 50% 40% at 80% 20%, var(--gradient-overlay-2) 0%, transparent 60%);
      pointer-events: none;
      z-index: 0;
    }

    .loore-hero {
      /* Content-sized hero (NOT forced to full viewport). The scroll line
         sits a fixed ~40px below the CTA as part of this block — always
         visible and close to the content on every screen — and the first
         narrative section follows directly, so there's no dead band and
         it "pops up" right away on scroll. Earlier full-height + either
         absolute-bottom or margin-top:auto pinning put the line at the
         screen edge (detached / below the fold across vh/svh/dvh). The
         clamped top padding clears the fixed 56px navbar and keeps the
         title in the upper area. (#98) */
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: flex-start;
      position: relative;
      padding: clamp(5rem, 18vh, 14rem) 2rem clamp(3rem, 8vh, 6rem);
      text-align: center;
    }

    .loore-hero-grain {
      position: absolute;
      inset: 0;
      opacity: 0.03;
      background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E");
      background-size: 200px;
      pointer-events: none;
    }

    .loore-logo-mark {
      font-family: var(--serif);
      font-weight: 300;
      font-size: 1.1rem;
      letter-spacing: 0.35em;
      text-transform: uppercase;
      color: var(--text-muted);
      margin-bottom: 3rem;
      opacity: 0;
      animation: loore-fade-down 1s cubic-bezier(0.22,1,0.36,1) 0.2s forwards;
    }

    .loore-hero-title {
      font-family: var(--serif);
      font-weight: 300;
      font-size: clamp(2.4rem, 6vw, 4.5rem);
      line-height: 1.15;
      max-width: 800px;
      color: var(--text-primary);
      margin-bottom: 1rem;
      opacity: 0;
      animation: loore-fade-down 1.2s cubic-bezier(0.22,1,0.36,1) 0.4s forwards;
    }

    .loore-hero-title em {
      font-style: italic;
      color: var(--accent);
    }

    .loore-hero-subtitle {
      font-family: var(--sans);
      font-weight: 300;
      font-size: clamp(1rem, 2vw, 1.15rem);
      color: var(--text-secondary);
      max-width: 520px;
      line-height: 1.7;
      margin-bottom: 2.8rem;
      opacity: 0;
      animation: loore-fade-down 1.2s cubic-bezier(0.22,1,0.36,1) 0.7s forwards;
    }

    .loore-scroll-hint {
      /* In normal flow, a fixed ~40px below the CTA (it follows the CTA in
         the JSX). Not absolute and not margin-top:auto — both put the line
         at the screen edge on tall viewports. Tied to the content, it's
         reliably visible and close to the CTA on every screen. (#98) */
      margin: 2.5rem auto 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0.35rem;
      opacity: 0;
      animation: loore-fade-down 1s ease 1.5s forwards;
      cursor: default;
    }

    .loore-scroll-line {
      width: 2px;
      height: 40px;
      /* Brighter, more opaque gradient so the cue reads clearly (was
         fading from the very dim --text-muted); still tapers off. */
      background: linear-gradient(to bottom, var(--text-secondary), rgba(255,255,255,0.05));
      animation: loore-pulse 2s ease-in-out infinite;
    }

    @keyframes loore-fade-down {
      from { opacity: 0; transform: translateY(20px); }
      to { opacity: 1; transform: translateY(0); }
    }

    @keyframes loore-pulse {
      0%, 100% { opacity: 0.5; }
      50% { opacity: 1; }
    }

    /* Narrative sections */
    .loore-narrative {
      position: relative;
      z-index: 1;
      padding: 0 2rem;
    }

    .loore-section {
      max-width: 640px;
      margin: 0 auto;
      padding: 6rem 0;
      position: relative;
    }

    .loore-section-divider {
      width: 40px;
      height: 1px;
      background: var(--accent);
      opacity: 0.4;
      margin: 0 auto 3rem;
    }

    .loore-section-lead {
      font-family: var(--serif);
      font-weight: 400;
      font-size: clamp(1.6rem, 3.5vw, 2.2rem);
      line-height: 1.35;
      color: var(--text-primary);
      margin-bottom: 1.5rem;
    }

    .loore-section-lead em {
      font-style: italic;
      color: var(--accent);
    }

    .loore-section-body {
      font-family: var(--sans);
      font-weight: 300;
      font-size: 1.05rem;
      line-height: 1.85;
      color: var(--text-secondary);
    }

    /* Preview mockup */
    .loore-preview {
      max-width: 760px;
      margin: 2rem auto 0;
      padding: 6rem 2rem 8rem;
      text-align: center;
      position: relative;
      z-index: 1;
    }

    .loore-preview-label {
      font-family: var(--sans);
      font-weight: 400;
      font-size: 0.75rem;
      letter-spacing: 0.2em;
      text-transform: uppercase;
      color: var(--text-muted);
      margin-bottom: 2.5rem;
    }

    .loore-mockup {
      background: var(--bg-card);
      border: 1px solid var(--border);
      border-radius: 12px;
      overflow: hidden;
      box-shadow: 0 20px 80px rgba(0,0,0,0.4);
    }

    .loore-mockup-bar {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 14px 18px;
      background: var(--bg-surface);
      border-bottom: 1px solid var(--border);
    }

    .loore-mockup-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: var(--bg-card-hover);
    }

    .loore-mockup-content {
      padding: 2.5rem 2rem;
      text-align: left;
    }

    .loore-mockup-date {
      font-family: var(--sans);
      font-size: 0.75rem;
      color: var(--text-muted);
      letter-spacing: 0.08em;
      margin-bottom: 1.2rem;
    }

    .loore-mockup-entry {
      font-family: var(--serif);
      font-size: 1.25rem;
      line-height: 1.75;
      color: var(--text-secondary);
      font-style: normal;
      font-weight: 400;
      margin-bottom: 1.8rem;
      padding-bottom: 1.8rem;
      border-bottom: 1px solid var(--border);
    }

    .loore-mockup-reflection-label {
      font-family: var(--sans);
      font-size: 0.7rem;
      letter-spacing: 0.15em;
      text-transform: uppercase;
      color: var(--accent);
      opacity: 0.7;
      margin-bottom: 0.8rem;
    }

    .loore-mockup-reflection {
      font-family: var(--sans);
      font-weight: 300;
      font-size: 0.95rem;
      line-height: 1.75;
      color: var(--text-muted);
    }

    /* Closing */
    .loore-closing {
      text-align: center;
      padding: 4rem 2rem 8rem;
      position: relative;
      z-index: 1;
    }

    .loore-closing-text {
      font-family: var(--serif);
      font-weight: 300;
      font-size: clamp(1.5rem, 3vw, 2rem);
      color: var(--text-secondary);
      max-width: 550px;
      margin: 0 auto 2.5rem;
      line-height: 1.4;
    }

    .loore-closing-text strong {
      color: var(--text-primary);
      font-weight: 400;
    }

    .loore-footer {
      text-align: center;
      padding: 2rem;
      font-family: var(--sans);
      font-size: 0.8rem;
      color: var(--text-muted);
      border-top: 1px solid var(--border);
      position: relative;
      z-index: 1;
    }

    @media (max-width: 640px) {
      .loore-section { padding: 4rem 0; }
      .loore-preview { padding: 3rem 1rem 5rem; }
      .loore-mockup-content { padding: 1.5rem 1.2rem; }

      /* Mobile hero: a touch tighter top than the base rule. Logo margin
         trimmed so the title sits in the upper third. The line stays
         ~40px below the CTA (base rule). (#98) */
      .loore-hero {
        padding-top: 12vh;
        padding-bottom: 2rem;
      }
      .loore-logo-mark { margin-bottom: 1.5rem; }
    }
  `,
};

function LandingPage() {
  return (
    <>
      <style>{styles.global}</style>
      <div className="loore-landing">
        {/* Grain overlay */}
        <div className="loore-hero-grain" />

        {/* Hero */}
        <section className="loore-hero">
          <div className="loore-logo-mark">Loore</div>
          <h1 className="loore-hero-title">
            Uncover your <em>lore</em>.
            <br />
            Author yourself.
          </h1>
          <p className="loore-hero-subtitle">
            A tool for seeing the story you're actually living — and shaping it
            with intention.
          </p>
          <div style={{
            opacity: 0,
            animation: "loore-fade-down 1.2s cubic-bezier(0.22,1,0.36,1) 0.9s forwards",
          }}>
            <CtaButton href="/login?returnUrl=%2F">Join the Alpha</CtaButton>
          </div>
          <div className="loore-scroll-hint" aria-hidden="true">
            <div className="loore-scroll-line" />
          </div>
        </section>

        {/* Narrative sections */}
        <div className="loore-narrative">
          {/* Section 1 */}
          <div className="loore-section">
            <FadeSection>
              <div className="loore-section-divider" />
            </FadeSection>
            <FadeSection delay={0.1}>
              <h2 className="loore-section-lead">
                You are already living a <em>story</em>.
              </h2>
            </FadeSection>
            <FadeSection delay={0.2}>
              <p className="loore-section-body">
                But most of it runs beneath awareness — patterns inherited,
                narratives distorted, intentions half-formed. Loore helps you
                uncover your own lore: the actual shape of your life, not just
                the story you tell yourself.
              </p>
            </FadeSection>
          </div>

          {/* Section 2 */}
          <div className="loore-section">
            <FadeSection>
              <div className="loore-section-divider" />
            </FadeSection>
            <FadeSection delay={0.1}>
              <h2 className="loore-section-lead">
                Surface what's <em>hidden</em>.
              </h2>
            </FadeSection>
            <FadeSection delay={0.2}>
              <p className="loore-section-body">
                Through effortless journaling and AI reflection, you name what's
                vague, see through your blind spots, and begin to author yourself
                more deliberately. This is private, first. Sacred, even.
              </p>
            </FadeSection>
          </div>

          {/* Section 3 */}
          <div className="loore-section">
            <FadeSection>
              <div className="loore-section-divider" />
            </FadeSection>
            <FadeSection delay={0.1}>
              <h2 className="loore-section-lead">
                Your lore becomes an <em>offering</em>.
              </h2>
            </FadeSection>
            <FadeSection delay={0.2}>
              <p className="loore-section-body">
                As you clarify who you are and what you're here for, sharing becomes
                natural — not performance, but offering. Your lore becomes part
                of a larger weave. A way of connecting that stems from authenticity.
              </p>
            </FadeSection>
          </div>
        </div>

        {/* Interface preview */}
        <div className="loore-preview">
          <FadeSection>
            <div className="loore-preview-label">A glimpse inside</div>
          </FadeSection>
          <FadeSection delay={0.15}>
            <div className="loore-mockup">
              <div className="loore-mockup-bar">
                <div className="loore-mockup-dot" />
                <div className="loore-mockup-dot" />
                <div className="loore-mockup-dot" />
              </div>
              <div className="loore-mockup-content">
                <div className="loore-mockup-date">February 8, 2026</div>
                <div className="loore-mockup-entry">
                  I keep reaching for control in situations where what I
                  actually need is to trust the process. The pattern is clear
                  now — every time something matters, I tighten my grip instead
                  of opening my hands...
                </div>
                <div className="loore-mockup-reflection-label">
                  Loore reflects
                </div>
                <div className="loore-mockup-reflection">
                  You've named this pattern three times this month, each time
                  with more precision. Notice the shift: the first entry
                  described frustration with outcomes. Now you're seeing the
                  mechanism itself. That's authorship beginning.
                </div>
              </div>
            </div>
          </FadeSection>
        </div>

        {/* Closing CTA */}
        <div className="loore-closing">
          <FadeSection>
            <p className="loore-closing-text">
              AI is rapidly gaining agency.
              <br />
              <strong>Loore helps you gain yours.</strong>
            </p>
          </FadeSection>
          <FadeSection delay={0.15}>
            <CtaButton href="/login?returnUrl=%2F">Begin your lore</CtaButton>
          </FadeSection>
          <FadeSection delay={0.25}>
            <a href="/why-loore" style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.6rem",
              marginTop: "1.5rem",
              fontFamily: "var(--sans)",
              fontWeight: 300,
              fontSize: "0.92rem",
              padding: "12px 0",
              color: "var(--text-muted)",
              textDecoration: "none",
              borderBottom: "1px solid var(--border)",
            }}>
              Why Loore &rarr;
            </a>
          </FadeSection>
        </div>

        <footer className="loore-footer">
          © {new Date().getFullYear()} Loore
        </footer>
      </div>
    </>
  );
}

export default LandingPage;
